import { AvatarBlock, Button, Heading, Spacer, Steps } from '@ksyos/design-system';
import { useBlocker, useNavigate, useParams } from '@ksyos/router';
import { ConfirmationModal } from 'frontend/components/elements/modals/ConfirmationModal';
import { RightButtons } from 'frontend/components/elements/RightButtons';
import { flatten, omit } from 'lodash';
import { useEffect, useState } from 'react';
import type { EpisodeCategory } from '@common/enums';
import type { IAddCarePathServiceReaderSettings } from '@common/interfaces/addCarePathServicesSettings';
import type { IAddCarePathSettings } from '@common/interfaces/addCarePathSettings';
import type { IEpisodeCategoryBasic } from '@common/interfaces/episodeCategory';
import type { IServicesByServiceGroupForEpisodeCategory } from '@common/interfaces/servicesByServiceGroupForEpisodeCategory';
import type { IPractitionerAddCarePathSaveableServiceValidated } from '@common/validation/addCarePath';
import type { ICareProviderAddCarePathState } from '@frontend/components/interfaces/careProviderAddCarePathState';
import type { IEmployment } from '@frontend/components/interfaces/employment';
import type { IOrganization } from '@frontend/components/interfaces/organization';
import type { IPractitioner } from '@frontend/components/interfaces/practitioner';
import type { ValidationErrors } from '@frontend/utils/validation';
import { ManagerAuthorization, Profession } from '@common/enums';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { padStartBigCode } from '@common/utils/padStartBigCode';
import { addCarePathServiceSettingsSchema, practitionerCarePathAddSchema } from '@common/validation/addCarePath';
import { employmentPractitionerRegistrationCodesBackendSchema } from '@common/validation/employmentPractitionerRegistrationCodes';
import { registrationCodesFrontendSchema } from '@common/validation/practitioner';
import {
    AddCarePathStep,
    addCarePathSteps,
    ChooseCarePathStep,
} from '@frontend/components/interfaces/careProviderAddCarePathState';
import { OverlayTemplate } from '@frontend/components/templates/OverlayTemplate';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils';
import { useAbortableFetch } from '@frontend/utils/hooks/useAbortableFetch';
import {
    getSpecialismCodeByProfession,
    isIdentifierDeclarationOrSupervisorRequired,
    isIdentifierDeclarationRequired,
    isSpecialismRequired,
    isSupervisorRequired,
} from '@frontend/utils/practitionerIdentifiersHelpers';
import { checkDuplicatedRegistrationCodes } from '@frontend/utils/registrationCodesValidations';
import { emptyValidationErrors, validate } from '@frontend/utils/validation';
import { ChooseCarePath } from './ChooseCarePath';
import { OrganizationSettings } from './OrganizationSettings';
import { PersonalSettingsAddCarePathContext } from './personalSettingsAddCarePathContext';
import { SuccessAndSummary } from './SuccesAndSummary';

type NextOrSave = 'next' | 'save';

export function PersonalSettingsAddCarePath() {
    assertHasAuthorization(ManagerAuthorization.employmentSettingsEdit);
    const { employmentId } = useParams();
    const navigate = useNavigate();
    const { episodeCategory } = useParams() as { episodeCategory: EpisodeCategory };
    const abortableFetch = useAbortableFetch();

    const initialState: ICareProviderAddCarePathState = {
        carePathStep: ChooseCarePathStep.chooseEpisodeCategory,
        currentStep: AddCarePathStep.chooseCarePath,
        preSelectedEpisodeCategoryName: episodeCategory,
    };

    const [state, setState] = useState<ICareProviderAddCarePathState>(initialState);
    const [nextCarePathStepOrSave, setNextCarePathStepOrSave] = useState<NextOrSave>('save');
    const [employment, setEmployment] = useState<IEmployment>();
    const [organization, setOrganization] = useState<IOrganization>();
    const [practitioner, setPractitioner] = useState<IPractitioner>();
    const [serviceGroupMembershipsGroupedByEpisodeCategories, setServiceGroupMembershipsGroupedByEpisodeCategories] =
        useState<IServicesByServiceGroupForEpisodeCategory[]>([]);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isMissingPreferredServiceOrganizationConsumers, setIsMissingPreferredServiceOrganizationConsumers] =
        useState<string[]>([]);
    const [registrationCodesError, setRegistrationCodesError] = useState(false);
    const [isProfessionSelectionNeeded, setIsProfessionSelectionNeeded] = useState(false);
    const [defaultProfileSettings, setDefaultProfileSettings] = useState<IAddCarePathSettings>();

    const defaultReturnUrl = `/employments/${employmentId}/personalSettings/carePaths`;
    const [isBlocked, setIsBlocked] = useState(true);
    const blocker = useBlocker(isBlocked);

    useEffect(() => {
        load();
    }, [employmentId]);

    useEffect(() => {
        setNextCarePathStep();
    }, [state.episodeCategory, state.serviceGroups, state.currentStep]);

    if (!state.practitioner || !organization || !state.employment) {
        return null;
    }

    if (state.currentStep === AddCarePathStep.succesAndSummary) {
        return (
            <OverlayTemplate>
                <SuccessAndSummary
                    addCarePathState={state}
                    organization={organization}
                />
            </OverlayTemplate>
        );
    }

    return (
        <>
            <OverlayTemplate
                leftButtons={
                    <Button
                        onClick={() => navigate(defaultReturnUrl)}
                        variant="outline"
                    >
                        {translateKey('common.cancelling')}
                    </Button>
                }
                rightButtons={renderRightButtons()}
            >
                <Spacer spacing="large">
                    {renderHeader()}
                    <AvatarBlock
                        backgroundColor="white"
                        icon="practitioner-nogender"
                        imageUrl={state.practitioner.profilePictureUrl}
                        initials={getDisplayInitials(state.practitioner)}
                        size="medium"
                        subtitle={organization.name}
                        title={getDisplayName(state.practitioner, false, 'nameGivenNameFamily')}
                    />
                </Spacer>
                {state.currentStep === AddCarePathStep.organizationSettings && (
                    <Steps
                        activeStep={state.currentStep}
                        sx={{ mb: 6 }}
                    >
                        {addCarePathSteps.map((step, index) => (
                            <Steps.Step
                                key={step.title}
                                label={step.title}
                                onClick={() => handleBreadCrumbSteps(index)}
                            />
                        ))}
                    </Steps>
                )}
                {renderSteps()}
            </OverlayTemplate>

            <ConfirmationModal
                handleNo={() => blocker.reset?.()}
                handleYes={() => {
                    blocker.proceed?.();
                }}
                isOpen={blocker.state === 'blocked'}
                question={translateKey('episodeCategory.confirmCancelMessage')}
                title={translateKey('episodeCategory.confirmCancelTitle')}
            />
        </>
    );

    function renderHeader() {
        const text = episodeCategory
            ? translateKey(
                  'episodeCategory.addServiceForEpisodeCategory',
                  translateKey(`episodeCategoryEnum.${episodeCategory}`)
              )
            : translateKey('episodeCategory.addEpisodeCategory');

        return <Heading size="xlarge">{text}</Heading>;
    }

    function renderSteps() {
        if (state.currentStep === AddCarePathStep.chooseCarePath) {
            if (!state.employment || !state.practitioner) {
                return null;
            }

            return (
                <PersonalSettingsAddCarePathContext.Provider
                    value={{ defaultProfileSettings, setDefaultProfileSettings }}
                >
                    <ChooseCarePath
                        addCarePathState={state}
                        employment={employment}
                        isProfessionSelectionNeeded={isProfessionSelectionNeeded}
                        organization={organization}
                        practitioner={practitioner}
                        serviceGroupMembershipsGroupedByEpisodeCategories={
                            serviceGroupMembershipsGroupedByEpisodeCategories
                        }
                        serviceIdsNotContainingPreferredServiceOrganizationConsumers={
                            isMissingPreferredServiceOrganizationConsumers
                        }
                        showRegistrationCodesError={registrationCodesError}
                        updateState={updateState}
                    />
                </PersonalSettingsAddCarePathContext.Provider>
            );
        }

        if (state.currentStep === AddCarePathStep.organizationSettings) {
            return (
                <OrganizationSettings
                    errors={errors}
                    state={state}
                    updateState={updateState}
                />
            );
        }
    }

    function renderRightButtons() {
        return (
            <RightButtons
                currentStep={state.currentStep}
                isDisabled={isNextOrSaveStepDisabled()}
                nextStepLabel={nextCarePathStepOrSave === 'save' ? translateKey('personalSettings.save') : undefined}
                onClickNextStep={nextCarePathStepOrSave === 'save' ? onSave : goToNextStep}
                onClickPreviousStep={goToPreviousStep}
            />
        );
    }

    function getMissingPreferredServiceOrganizationConsumers() {
        const missingPreferredServiceOrganizationConsumers = [];

        for (const serviceGroup of state.serviceGroups ?? []) {
            if (serviceGroup.profileSettings) {
                for (const serviceProviderSettings of serviceGroup.profileSettings.serviceProviderSettings ?? []) {
                    if (serviceProviderSettings.isServiceConsumerSelector && !serviceProviderSettings.isDisabled) {
                        const preferredServiceOrganizationConsumers =
                            serviceGroup.preferredServiceOrganizationConsumers?.[serviceProviderSettings.service.id];
                        if (
                            !preferredServiceOrganizationConsumers ||
                            preferredServiceOrganizationConsumers.length === 0
                        ) {
                            missingPreferredServiceOrganizationConsumers.push(serviceProviderSettings.service.id);
                        }
                    }
                }
            }
        }

        return missingPreferredServiceOrganizationConsumers;
    }

    async function goToNextStep() {
        if ((await areRegistrationCodesCorrect()) === false) {
            return;
        }

        const missingPreferredServiceOrganizationConsumers = getMissingPreferredServiceOrganizationConsumers();

        if (missingPreferredServiceOrganizationConsumers.length > 0) {
            setIsMissingPreferredServiceOrganizationConsumers(missingPreferredServiceOrganizationConsumers);
        } else {
            setState((prevState) => ({ ...prevState, currentStep: AddCarePathStep.organizationSettings }));
        }
    }

    function handleBreadCrumbSteps(step: number) {
        setState((prevState) => ({ ...prevState, currentStep: step }));
    }

    function getServiceReaderSettings() {
        const serviceReaderSettings = flatten(
            (state.serviceGroups ?? []).map((sg) => sg.profileSettings?.serviceReaderSettings ?? [])
        );

        if (serviceReaderSettings.length > 0) {
            return serviceReaderSettings[0];
        }
    }

    function createSaveObject() {
        if (!state.serviceGroups || !state.employment || !state.episodeCategory) {
            return;
        }

        const saveObject: {
            employmentId: string;
            episodeCategoryId: string;
            organizationId: string;
            serviceReaderSettings?: IAddCarePathServiceReaderSettings;
            services: IPractitionerAddCarePathSaveableServiceValidated[];
        } = {
            employmentId: state.employment.id,
            episodeCategoryId: state.episodeCategory.id,
            organizationId: state.employment.organizationId,
            services: [],
        };

        saveObject.serviceReaderSettings = getServiceReaderSettings();

        for (const serviceGroup of state.serviceGroups) {
            for (const service of serviceGroup.services ?? []) {
                const serviceConsumerSettings = (serviceGroup.profileSettings?.serviceConsumerSettings ?? []).find(
                    (serviceConsumerSetting) =>
                        serviceConsumerSetting.service.id === service.id && !serviceConsumerSetting.isDisabled
                );
                const serviceProviderSettings = (serviceGroup.profileSettings?.serviceProviderSettings ?? []).find(
                    (serviceProviderSetting) =>
                        serviceProviderSetting.service.id === service.id && !serviceProviderSetting.isDisabled
                );
                const serviceSupervisorSettings = (serviceGroup.profileSettings?.serviceSupervisorSettings ?? []).find(
                    (serviceSupervisorSetting) =>
                        serviceSupervisorSetting.service.id === service.id && !serviceSupervisorSetting.isDisabled
                );
                const serviceOrganizationSettings = (serviceGroup.serviceOrganizationSettings ?? []).find(
                    (serviceOrganizationSetting) => serviceOrganizationSetting.serviceId === service.id
                );
                const preferredServiceOrganizationConsumers =
                    serviceGroup.preferredServiceOrganizationConsumers?.[service.id];
                const serviceEmploymentSettings = (serviceGroup.profileSettings?.serviceEmploymentSettings ?? []).find(
                    (serviceEmploymentSetting) => serviceEmploymentSetting.service.id === service.id
                );

                if (
                    serviceConsumerSettings ||
                    serviceProviderSettings ||
                    serviceSupervisorSettings ||
                    serviceEmploymentSettings
                ) {
                    saveObject.services.push({
                        ...omit(service, 'name'),
                        preferredServiceOrganizationConsumers,
                        serviceConsumerSettings,
                        serviceEmploymentSettings,
                        serviceOrganizationSettings,
                        serviceProviderSettings,
                        serviceSupervisorSettings,
                    } as IPractitionerAddCarePathSaveableServiceValidated);
                }
            }
        }

        return saveObject;
    }

    function goToPreviousStep() {
        setErrors(emptyValidationErrors);
        setState((prevState) => ({ ...prevState, currentStep: AddCarePathStep.chooseCarePath }));
    }

    async function onSave() {
        if (!state.serviceGroups || !state.employment || (await areRegistrationCodesCorrect()) === false) {
            return;
        }

        if (isProfessionSelectionNeeded) {
            await saveProfessionAndRegistrationCodes();
        }

        await saveCarePath();
    }

    async function saveProfessionAndRegistrationCodes() {
        if (!state.employment || !state.practitioner) {
            return;
        }

        const registrationCodes = {
            hasDeclarableQualification: state.practitioner.hasDeclarableQualification,
            identifierDeclaration: state.practitioner.identifierDeclaration,
            identifierDeclarationPractitionerId: state.employment.identifierDeclarationPractitionerId,
            identifierRegistrationBig: state.practitioner.identifierRegistrationBig,
            profession: state.employment.profession,
            specialismCode: getSpecialismCodeByProfession(state.employment.profession, state.employment.specialismCode),
        };
        const registrationCodesUpdate = { ...registrationCodes };
        registrationCodesUpdate.identifierRegistrationBig = padStartBigCode(
            registrationCodesUpdate.identifierRegistrationBig
        );

        const { errors: validationErrors, item } = validate(
            employmentPractitionerRegistrationCodesBackendSchema,
            registrationCodesUpdate
        );

        if (validationErrors.size > 0) {
            throw new Error('Registration codes validation failed');
        } else {
            const url = `/api/employments/${state.employment.id}/registrationCodes`;
            await abortableFetch.fetchJson(url, 'POST', item);
        }
    }

    async function saveCarePath() {
        if (!state.serviceGroups || !state.employment || !state.episodeCategory) {
            return;
        }

        const saveObject = createSaveObject();
        const { errors: validationErrors, item } = validate(practitionerCarePathAddSchema, saveObject);

        if (validationErrors.size > 0) {
            setErrors(validationErrors);
        } else {
            const url = `/api/employments/${state.employment.id}/addCarePath`;
            await abortableFetch.fetchJson(url, 'POST', item);

            setIsBlocked(false);
            setState((prevState) => ({ ...prevState, currentStep: AddCarePathStep.succesAndSummary }));
        }
    }

    function isNextOrSaveStepDisabled(): boolean {
        if (state.currentStep === AddCarePathStep.chooseCarePath) {
            if (
                !(state.serviceGroups?.length && state.serviceGroups.every((serviceGroup) => serviceGroup.profile?.id))
            ) {
                return true;
            }
        }

        return false;
    }

    async function areRegistrationCodesCorrect() {
        let correct = true;

        if (state.employment && state.practitioner && isProfessionSelectionNeeded) {
            if (!state.employment.profession || state.employment.profession === Profession.other) {
                correct = false;
            } else {
                const professionsArray = [state.employment.profession];
                const identifierDeclarationRequired =
                    isIdentifierDeclarationRequired(professionsArray) && !state.practitioner.identifierDeclaration;
                const supervisorRequired =
                    isSupervisorRequired(professionsArray) && !state.employment.identifierDeclarationPractitionerId;
                const identifierDeclarationOrSupervisorRequired =
                    isIdentifierDeclarationOrSupervisorRequired(professionsArray) &&
                    !state.practitioner.identifierDeclaration &&
                    !state.employment.identifierDeclarationPractitionerId;
                const specialismCodeRequired =
                    isSpecialismRequired(state.employment.profession) && !state.employment.specialismCode;

                if (
                    identifierDeclarationRequired ||
                    supervisorRequired ||
                    identifierDeclarationOrSupervisorRequired ||
                    specialismCodeRequired
                ) {
                    correct = false;
                } else {
                    const practitionerToCheck = { ...state.practitioner, professions: professionsArray };
                    const registrationCodeErrors = validate(registrationCodesFrontendSchema, practitionerToCheck);
                    const duplicatedRegistrationCodeErrors = await checkDuplicatedRegistrationCodes(
                        state.practitioner,
                        state.practitioner.id,
                        ['identifierDeclaration', 'identifierRegistrationBig']
                    );

                    if (registrationCodeErrors.hasErrors || duplicatedRegistrationCodeErrors.size > 0) {
                        correct = false;
                    }
                }
            }
        }

        setRegistrationCodesError(!correct);

        return correct;
    }

    function updateState(update: Partial<ICareProviderAddCarePathState>, carePathStep: ChooseCarePathStep) {
        let newState = { ...state, ...update };
        switch (carePathStep) {
            case ChooseCarePathStep.chooseEpisodeCategory:
                newState = updateEpisodeCategory(newState);
                break;
            case ChooseCarePathStep.chooseProfession:
                newState = updateProfession(newState);
                break;
            case ChooseCarePathStep.addServiceGroups:
                newState = updateServiceGroups(newState);
                break;
            case ChooseCarePathStep.addRoles:
                newState = updateRoles(newState);
                break;
            case ChooseCarePathStep.addPreferredServiceConsumers:
                newState = updatePreferredServiceConsumers(newState);
                break;
            case ChooseCarePathStep.updateServiceOrganizationSettings:
                newState = updateServiceOrganizationSettings(newState);
                break;
            default:
                break;
        }
        setState(newState);
    }

    function updateServiceOrganizationSettings(carePathState: ICareProviderAddCarePathState) {
        const serviceOrganizationSettings = flatten(
            state.serviceGroups?.map((serviceGroup) => serviceGroup.serviceOrganizationSettings ?? [])
        );
        const { errors: validationErrors } = validate(addCarePathServiceSettingsSchema, {
            serviceOrganizationSettings,
        });
        setErrors(validationErrors);

        return carePathState;
    }

    function updateEpisodeCategory(carePathState: ICareProviderAddCarePathState) {
        let newState = { ...carePathState };

        if (newState.episodeCategory) {
            let nextStep = ChooseCarePathStep.addServiceGroups;

            if (newState.employment && needsToChooseProfession(newState.employment)) {
                nextStep = ChooseCarePathStep.chooseProfession;
            }

            newState = {
                ...newState,
                carePathStep: nextStep,
                serviceGroups: undefined,
            };
        }

        return newState;
    }

    function needsToChooseProfession(empl: IEmployment) {
        return !empl.profession || empl.profession === Profession.other;
    }

    function updateServiceGroups(carePathState: ICareProviderAddCarePathState) {
        return carePathState;
    }

    function updateProfession(carePathState: ICareProviderAddCarePathState) {
        return { ...carePathState, carePathStep: ChooseCarePathStep.addServiceGroups };
    }

    function updateRoles(carePathState: ICareProviderAddCarePathState) {
        return carePathState;
    }

    function updatePreferredServiceConsumers(carePathState: ICareProviderAddCarePathState) {
        return carePathState;
    }

    function setNextCarePathStep() {
        let carePathNextStep: NextOrSave = 'save';

        if (state.currentStep === AddCarePathStep.chooseCarePath) {
            if (state.serviceGroups?.length) {
                const hasMissingServiceOrganizationSettings = state.serviceGroups.some(
                    (serviceGroup) => serviceGroup.profile?.id && serviceGroup.serviceOrganizationSettings?.length
                );
                if (hasMissingServiceOrganizationSettings) {
                    carePathNextStep = 'next';
                }
            }
        }

        setNextCarePathStepOrSave(carePathNextStep);
    }

    async function load() {
        const employmentFetchResult = await abortableFetch.fetchJson(`/api/employments/${employmentId}`, 'GET');
        const employmentResult = employmentFetchResult.json as IEmployment;
        setEmployment(employmentResult);
        setIsProfessionSelectionNeeded(needsToChooseProfession(employmentResult));

        const [, practitionerResult, , episodeCategoryResult] = await Promise.all([
            loadAvailableCarePathsWithServiceGroups(),
            getPractitioner(employmentResult.practitionerId),
            loadOrganization(employmentResult.organizationId),
            getEpisodeCategory(episodeCategory),
        ]);

        setPractitioner(practitionerResult);

        setState(
            updateEpisodeCategory({
                ...state,
                employment: employmentResult,
                episodeCategory: episodeCategoryResult,
                practitioner: practitionerResult,
            })
        );
    }

    async function getPractitioner(practitionerId: string) {
        const result = await abortableFetch.fetchJson(
            `/api/practitioners/${practitionerId}/?includeProfileThumbnail=true`,
            'GET'
        );

        return result.json as IPractitioner;
    }

    async function loadOrganization(organizationId: string) {
        const organizationUrl = `/api/organizations/${organizationId}`;
        const organizationResult = await abortableFetch.fetchJson(organizationUrl, 'GET');
        setOrganization(organizationResult.json);
    }

    async function getEpisodeCategory(name?: string) {
        if (name) {
            const episodeCategoriesUrl = `/api/episodeCategories?name=${name}`;
            const episodeCategoriesResult = await abortableFetch.fetchJson(episodeCategoriesUrl, 'GET');

            return episodeCategoriesResult.json.items[0] as IEpisodeCategoryBasic | undefined;
        }

        return undefined;
    }

    async function loadAvailableCarePathsWithServiceGroups() {
        const episodeCategoryQueryString = episodeCategory ? `?episodeCategory=${episodeCategory}` : '';
        const url = `/api/employments/${employmentId}/addableServiceGroups${episodeCategoryQueryString}`;
        const result = await abortableFetch.fetchJson(url, 'GET');
        setServiceGroupMembershipsGroupedByEpisodeCategories(result.json.items);
    }
}
